import { useCallback, useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button } from '@/src/renderer/components/ui/Button'
import { Input } from '@/src/renderer/components/ui/Input'
import { PasswordInput } from '@/src/renderer/components/ui/PasswordInput'
import { NotebookIcon } from '@/src/renderer/components/icons/NotebookIcon'
import { ProfilePictureIcon } from '@/src/renderer/components/icons/ProfilePictureIcon'
import { FacePlusIcon } from '@/src/renderer/components/icons/FacePlusIcon'
import { getPlatformAPI } from '../../../lib/platform'
import { updateUserProfile } from '../../../lib/supabase/auth'
import { updateUserAvatarPreset } from '../../../lib/supabase/avatars'
import { supabase } from '../../../lib/supabase/client'
import { db } from '../../../lib/db'
import { AvatarPickerPopover } from '@/src/renderer/components/common/AvatarPickerPopover'
import { updateSetupFlowProgress } from '../../../lib/supabase/setupFlow'
import './SetupProfilePage.scss'

/**
 * SetupProfilePage Component
 *
 * Profile setup page shown after successful OTP verification during registration.
 * Collects user's full name and password to complete their profile.
 *
 * @remarks
 * Features:
 * - Full name input with validation (required)
 * - Password input with minimum length validation (8 characters)
 * - Profile photo placeholder (future feature)
 * - Auto-focus on name input
 * - Enter key navigation between fields
 * - Updates Supabase user metadata
 * - Sets cloud-sync storage mode (Electron only)
 *
 * Validation Rules:
 * - Name: Cannot be empty
 * - Password: Minimum 8 characters
 *
 * Navigation Flow:
 * - Previous: OTP Verification (SetupPage)
 * - Next: Usage Questionary Page
 *
 * @example
 * ```tsx
 * <Route path="/setup-profile" element={
 *   <ProtectedRoute>
 *     <SetupProfilePage />
 *   </ProtectedRoute>
 * } />
 * ```
 */
export default function SetupProfilePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const platformAPI = getPlatformAPI()

  // Get saved data from navigation state (if coming back from purpose page)
  const locationState = location.state as {
    savedPurposes?: any[]
    savedIntents?: any[]
    profileData?: { fullName: string; password: string }
  }
  const savedPurposesFromState = locationState?.savedPurposes
  const savedIntentsFromState = locationState?.savedIntents
  const profileDataFromState = locationState?.profileData

  // Form States - Initialize with profileData from state if available
  const [fullName, setFullName] = useState(profileDataFromState?.fullName || '')
  const [password, setPassword] = useState(profileDataFromState?.password || '')
  const [nameError, setNameError] = useState(false)
  const [passwordError, setPasswordError] = useState(false)
  const [isPasswordFocused, setIsPasswordFocused] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [initialName, setInitialName] = useState('') // Track initial value for change detection
  const [initialPassword, setInitialPassword] = useState('') // Track initial value for change detection
  const [isFirstTimeSetup, setIsFirstTimeSetup] = useState(false) // Track if this is first-time setup from OTP/offline flow

  // Avatar Picker States
  const [isAvatarPickerOpen, setIsAvatarPickerOpen] = useState(false)
  const [selectedAvatarId, setSelectedAvatarId] = useState<string | null>(null)
  const [selectedAvatarUrl, setSelectedAvatarUrl] = useState<string | null>(null)

  // Refs
  const nameInputRef = useRef<HTMLInputElement>(null)
  const passwordInputRef = useRef<HTMLInputElement>(null)

  // Check if offline mode - only read once on mount
  const [isOfflineMode] = useState(() => {
    const loginMethod = localStorage.getItem('loginMethod')
    return loginMethod === 'offline'
  })

  // Platform-spezifische CSS-Klasse
  const platformClass = platformAPI.platform === 'electron' ? 'setup-profile-page--electron' : 'setup-profile-page--web'

  /**
   * Initialization effect
   * Loads existing profile data if available, sets focus on name input
   */
  useEffect(() => {
    const loadProfile = async () => {
      // Track page view (only for cloud users)
      const loginMethod = localStorage.getItem('loginMethod')
      if (loginMethod !== 'offline') {
        await updateSetupFlowProgress('profile')
      }

      // Check if this is first-time setup from OTP/offline flow
      const isFromSetupFlow = loginMethod === 'otp' || loginMethod === 'offline'
      setIsFirstTimeSetup(isFromSetupFlow)

      // If coming back with profileData from state, use that as initial values
      if (profileDataFromState) {
        setInitialName(profileDataFromState.fullName)
        setInitialPassword(profileDataFromState.password)
      } else if (supabase) {
        // Otherwise load from database
        try {
          const { data: { user } } = await supabase.auth.getUser()
          if (user?.user_metadata?.full_name) {
            const existingName = user.user_metadata.full_name
            setFullName(existingName)
            setInitialName(existingName)
          }
        } catch (error) {
          console.error('[SetupProfile] Error loading profile:', error)
        }
      }

      if (nameInputRef.current) {
        nameInputRef.current.focus()
      }
    }

    loadProfile()
    // Cleanup loginMethod flag only for OTP mode (nicht für offline mode!)
    // Im Offline-Modus muss der Flag erhalten bleiben für die Navigation
    const currentLoginMethod = localStorage.getItem('loginMethod')
    if (currentLoginMethod === 'otp') {
      localStorage.removeItem('loginMethod')
    }
  }, [])

  /**
   * Handle avatar picker button click.
   * Memoized so the `React.memo`-wrapped {@link AvatarPickerPopover} doesn't
   * re-render on every keystroke in the name input.
   */
  const handleChangePhotoClick = useCallback(() => {
    setIsAvatarPickerOpen(true)
  }, [])

  /**
   * Handle avatar selection from picker
   */
  const handleSelectAvatar = useCallback(async (avatarId: string, imageUrl: string) => {
    setSelectedAvatarId(avatarId)
    setSelectedAvatarUrl(imageUrl)

    // Save avatar to database. On failure the optimistic UI keeps the
    // chosen avatar visible (we already setSelected... above) but the
    // toast tells the user the persistence step didn't land — matches
    // the AccountProfileSection pattern so behaviour is consistent
    // between setup and preferences.
    try {
      if (!isOfflineMode && supabase) {
        // Online mode: Save to Supabase
        const { data: { user } } = await supabase.auth.getUser()
        if (user) {
          await updateUserAvatarPreset(user.id, avatarId)
        }
      } else {
        // Offline mode: Save to Dexie
        await db.userProfiles.update('offline-user', {
          avatar_preset_id: avatarId,
        })
      }
    } catch (error) {
      console.error('[SetupProfile] Failed to save avatar:', error)
      toast.error(t('profile.saveFailed'))
    }
  }, [isOfflineMode, t])

  const handleClosePicker = useCallback(() => setIsAvatarPickerOpen(false), [])

  /**
   * Handles form submission
   * Validates inputs, updates user profile if changed, and navigates to purpose page
   */
  const handleContinue = async () => {
    // Validierung
    if (!fullName.trim()) {
      setNameError(true)
      nameInputRef.current?.focus()
      return
    }

    // Passwort ist nur im Online-Modus erforderlich
    if (!isOfflineMode) {
      if (!password || password.length < 10) {
        setPasswordError(true)
        passwordInputRef.current?.focus()
        return
      }
    }

    setNameError(false)
    setPasswordError(false)
    setIsSubmitting(true)

    try {
      // Offline Mode: Save to Dexie
      if (isOfflineMode) {

        try {
          // Create or update offline user profile in Dexie
          await db.userProfiles.put({
            id: 'offline-user',
            fullName: fullName.trim(),
            username: null,
            email: null,
            displayName: null,
            avatar: null,
            usagePurposes: [], // Will be filled later
            userIntents: [], // Will be filled later
            createdAt: new Date(),
            updatedAt: new Date(),
            lastModified: new Date(),
            synced: false,
            syncedAt: null,
            preferences: {
              theme: 'system',
              language: 'de',
              notifications: true,
              sounds: true,
              autoSync: false,
              workingHours: { start: '09:00', end: '17:00' },
              pomodoroSettings: {
                workDuration: 25,
                shortBreakDuration: 5,
                longBreakDuration: 15,
                cyclesBeforeLongBreak: 4
              }
            },
            statistics: {
              totalTasksCompleted: 0,
              totalTimeSpent: 0,
              currentStreak: 0,
              longestStreak: 0,
              lastActiveDate: null
            }
          })

          // Also keep in localStorage for backward compatibility
          localStorage.setItem('offlineUserName', fullName.trim())
          // Device-level hint surviving logout, used by the login greeting
          localStorage.setItem('lastFullName', fullName.trim())

        } catch (error) {
          console.error('[SetupProfile] Error saving to Dexie:', error)
          // Fallback to localStorage only
          localStorage.setItem('offlineUserName', fullName.trim())
          localStorage.setItem('lastFullName', fullName.trim())
        }

        // Navigate to Purpose page
        navigate('/setup-purpose', {
          state: {
            savedPurposes: savedPurposesFromState,
            savedIntents: savedIntentsFromState,
            profileData: { fullName: fullName.trim(), password: '' }
          }
        })
        return
      }

      // Online Mode: Update database
      const nameChanged = fullName.trim() !== initialName
      // Only update password if:
      // 1. This is first-time setup from OTP/offline flow, OR
      // 2. Returning from navigation with profileData state
      // This prevents updating password when user has existing profile and autocomplete fills it
      const hasNavigationState = !!profileDataFromState
      const passwordChanged = password.length > 0 &&
                              password !== initialPassword &&
                              (isFirstTimeSetup || hasNavigationState)

      if (nameChanged || passwordChanged) {
        // Profil aktualisieren nur wenn sich etwas geändert hat
        const updateData: { fullName?: string; password?: string } = {}
        if (nameChanged) updateData.fullName = fullName.trim()
        if (passwordChanged) updateData.password = password

        const result = await updateUserProfile(updateData)

        if (result.success) {
          // Update initial values to prevent duplicate updates if user clicks again
          setInitialName(fullName.trim())
          setInitialPassword(password)

          // Session neu laden, damit user_metadata aktualisiert wird
          if (supabase) {
            const { data } = await supabase.auth.refreshSession()
          }

          // Cloud-Sync Mode setzen (nur Electron, nur beim ersten Mal)
          if (platformAPI.platform === 'electron' && !initialName) {
            await platformAPI.setStorageMode('cloud-sync')
          }

          // Entferne loginMethod Flag (Profil-Setup abgeschlossen)
          localStorage.removeItem('loginMethod')

          // Setze profileSetupCompleted Flag
          localStorage.setItem('profileSetupCompleted', 'true')

          // Kurzer Delay, damit AuthProvider die Session aktualisieren kann
          await new Promise(resolve => setTimeout(resolve, 100))

          // Navigate to Purpose page with saved purposes and profile data
          navigate('/setup-purpose', {
            state: {
              savedPurposes: savedPurposesFromState,
              savedIntents: savedIntentsFromState,
              profileData: { fullName: fullName.trim(), password }
            }
          })
        } else {
          // Fehler: Allgemeiner Update-Fehler
          console.error('[SetupProfile] Update error:', result.error)
          // TODO: Error-Message anzeigen
        }
      } else {
        // Keine Änderungen, direkt weiternavigieren mit aktuellen Daten
        navigate('/setup-purpose', {
          state: {
            savedPurposes: savedPurposesFromState,
            savedIntents: savedIntentsFromState,
            profileData: { fullName: fullName.trim(), password }
          }
        })
      }
    } catch (error) {
      console.error('[SetupProfile] Unexpected error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={`setup-profile-page ${platformClass}`}>
      {/* Scrollable Container */}
      <div className="setup-profile-page__scrollable-container">
        <div className="setup-profile-page__scrollable-inner">

          {/* Content Area */}
          <div className="setup-profile-page__content-area">
            {/* Left Side - Welcome */}
            <div className="setup-profile-page__left-section">
              <div className="setup-profile-page__left-content">
                {/* Notebook Icon */}
                <div className="setup-profile-page__notebook-icon">
                  <NotebookIcon />
                </div>
                <h1 className="setup-profile-page__title">
                  {t('setupProfile.title')}
                </h1>
                <p className="setup-profile-page__subtitle">
                  {t('setupProfile.subtitle')}
                </p>
              </div>
            </div>

            {/* Right Side - Form */}
            <div className="setup-profile-page__right-section">
              {/* Profile Photo */}
              <div className="setup-profile-page__photo-section">
                <div className="setup-profile-page__profile-photo">
                  <ProfilePictureIcon
                    selectedAvatarUrl={selectedAvatarUrl}
                  />
                </div>
                <div className="relative">
                  <Button
                    variant="outline"
                    size="small"
                    icon={<FacePlusIcon />}
                    onClick={handleChangePhotoClick}
                  >
                    {t('setupProfile.changePhoto')}
                  </Button>
                  <AvatarPickerPopover
                    isOpen={isAvatarPickerOpen}
                    onClose={handleClosePicker}
                    onSelectAvatar={handleSelectAvatar}
                    selectedAvatarId={selectedAvatarId}
                  />
                </div>
              </div>

              {/* Form */}
              <div className="setup-profile-page__form">
                {/* Name Input */}
                <Input
                  ref={nameInputRef}
                  type="text"
                  name="name"
                  autoComplete="name"
                  value={fullName}
                  onChange={(e) => {
                    setFullName(e.target.value)
                    setNameError(false)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      if (isOfflineMode) {
                        // In offline mode, submit directly (no password field)
                        handleContinue()
                      } else {
                        // In online mode, focus next input (password)
                        passwordInputRef.current?.focus()
                      }
                    }
                  }}
                  placeholder={t('setupProfile.namePlaceholder')}
                  error={nameError}
                />
                {nameError && (
                  <p className="setup-profile-page__error-message">{t('setupProfile.nameRequired')}</p>
                )}

                {/* Password Input - Only show in online mode */}
                {!isOfflineMode && (
                  <div className="setup-profile-page__password-wrapper">
                    <PasswordInput
                      ref={passwordInputRef}
                      name="password"
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value)
                        setPasswordError(false)
                      }}
                      onFocus={() => setIsPasswordFocused(true)}
                      onBlur={() => setIsPasswordFocused(false)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleContinue()
                        }
                      }}
                      placeholder={t('setupProfile.passwordPlaceholder')}
                      error={passwordError}
                    />
                    {(isPasswordFocused || passwordError || password.length > 0) && (
                      <p className={`setup-profile-page__password-hint ${passwordError ? 'setup-profile-page__password-hint--error' : ''}`}>
                        {t('setupProfile.passwordHint')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Bottom Action Bar */}
          <div className="setup-profile-page__action-bar">
            <div className="setup-profile-page__action-bar-content">
              <Button
                onClick={handleContinue}
                onMouseDown={(e) => e.preventDefault()}
                shortcut={{ variant: 'return' }}
                disabled={isSubmitting}
              >
                {isSubmitting ? t('setupProfile.saving') : t('setupProfile.continue')}
              </Button>
            </div>
          </div>
        </div>
      </div>

    </div>
  )
}
